<template>
  <div class="cancellation">
    <!-- Progress stepper / breadcrumb -->
    <div class="cancellation__stepper">
      <span class="cancellation__step cancellation__step--inactive">{{ $t('MENU.FOR_BUYERS') }}</span>
      <span class="cancellation__step-line"></span>
      <span class="cancellation__step cancellation__step--active">{{ $t('CANCELLATION.TITLE') }}</span>
    </div>

    <div class="cancellation__grid">
      <!-- Left: intro -->
      <div class="cancellation__intro">
        <h1 class="cancellation__title">{{ $t('CANCELLATION.TITLE') }}</h1>
        <p class="cancellation__desc">{{ $t('CANCELLATION.INTRO') }}</p>
      </div>

      <!-- Right: form -->
      <div class="cancellation__form-col">
        <form class="cancellation__form" @submit.prevent="submit">
          <!-- 1. Name -->
          <div class="cancellation__section">
            <label class="cancellation__label">{{ $t('CANCELLATION.NAME_LABEL') }}</label>
            <input
              v-model="form.name"
              type="text"
              class="cancellation__input"
              :class="{ 'cancellation__input--error': errors.name }"
              :placeholder="$t('CANCELLATION.NAME_PH')"
            />
            <p v-if="errors.name" class="cancellation__error">{{ errors.name[0] }}</p>
          </div>

          <!-- 2. Products -->
          <div class="cancellation__section">
            <label class="cancellation__label">{{ $t('CANCELLATION.PRODUCTS_LABEL') }}</label>
            <textarea
              v-model="form.products"
              rows="4"
              class="cancellation__textarea"
              :class="{ 'cancellation__textarea--error': errors.products }"
              :placeholder="$t('CANCELLATION.PRODUCTS_PH')"
            ></textarea>
            <p v-if="errors.products" class="cancellation__error">{{ errors.products[0] }}</p>
          </div>

          <!-- 3. Email -->
          <div class="cancellation__section">
            <label class="cancellation__label cancellation__label--spaced">{{ $t('CANCELLATION.EMAIL_LABEL') }}</label>
            <label class="cancellation__sublabel">{{ $t('CANCELLATION.EMAIL_SUBLABEL') }}</label>
            <input
              v-model="form.email"
              type="email"
              class="cancellation__input"
              :class="{ 'cancellation__input--error': errors.email }"
              :placeholder="$t('CANCELLATION.EMAIL_PH')"
            />
            <p v-if="errors.email" class="cancellation__error">{{ errors.email[0] }}</p>
          </div>

          <!-- Submission -->
          <div class="cancellation__submit">
            <label class="cancellation__terms">
              <span class="cancellation__cb">
                <input v-model="form.terms" type="checkbox" class="cancellation__cb-input" />
                <svg class="cancellation__cb-x" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2.5" />
                </svg>
              </span>
              <span class="cancellation__terms-text">
                {{ $t('CANCELLATION.TERMS_PREFIX') }}
                <router-link to="/termsconditions" target="_blank" @click.native.stop>{{
                  $t('CANCELLATION.TERMS_LINK')
                }}</router-link>.
              </span>
            </label>
            <p v-if="errors.terms" class="cancellation__error cancellation__error--terms">{{ errors.terms[0] }}</p>

            <button type="submit" class="cancellation__btn" :disabled="loading">
              {{ $t('CANCELLATION.SUBMIT') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data () {
    return {
      loading: false,
      form: {
        name: null,
        products: null,
        email: null,
        terms: false
      },
      errors: {}
    }
  },
  methods: {
    async submit () {
      this.errors = {}
      this.loading = true

      try {
        await this.$axios.post('cancellation-notice', this.form)
        this.$app.toast(this.$t('CANCELLATION.SUCCESS'), 5000)

        this.form = { name: null, products: null, email: null, terms: false }
      } catch (error) {
        if (error.status === 422 && error.data) {
          this.errors = error.data.errors
        }
      }

      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
// Design tokens — "Monochrome Gallery Ethos"
$primary: #000000;
$on-primary: #ffffff;
$on-surface-variant: #4c4546;
$outline: #7e7576;
$outline-variant: #cfc4c5;
$error: #ba1a1a;

.cancellation {
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 40px;
  color: #1b1c1c;

  @media (max-width: 767px) {
    padding: 48px 16px;
  }

  // --- Stepper ---
  &__stepper {
    display: flex;
    align-items: center;
    gap: 16px;
    max-width: 672px;
    margin-bottom: 64px;
  }

  &__step {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    white-space: nowrap;

    &--inactive {
      font-weight: 400;
      color: $outline;
    }

    &--active {
      font-weight: 700;
      color: $primary;
    }
  }

  &__step-line {
    flex: 1 1 auto;
    height: 1px;
    background: $outline-variant;
  }

  // --- Grid: 12 columns, left 4 / right 7 (offset 1) ---
  &__grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 24px;

    @media (max-width: 1023px) {
      grid-template-columns: 1fr;
      gap: 40px;
    }
  }

  &__intro {
    grid-column: span 4;

    @media (max-width: 1023px) {
      grid-column: auto;
    }
  }

  &__form-col {
    grid-column: 6 / span 7;

    @media (max-width: 1023px) {
      grid-column: auto;
    }
  }

  // --- Left column ---
  &__title {
    font-size: 32px;
    line-height: 40px;
    font-weight: 600;
    letter-spacing: -0.02em;
    text-transform: uppercase;
    margin: 0 0 24px;

    @media (max-width: 767px) {
      font-size: 28px;
      line-height: 34px;
    }
  }

  &__desc {
    font-size: 16px;
    line-height: 24px;
    color: $on-surface-variant;
    margin: 0;
  }

  // --- Form ---
  &__form {
    display: flex;
    flex-direction: column;
    gap: 48px;
  }

  &__section {
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: $primary;
    margin-bottom: 8px;

    &--spaced {
      margin-bottom: 16px;
    }
  }

  &__sublabel {
    font-size: 12px;
    line-height: 16px;
    color: $on-surface-variant;
    margin-bottom: 4px;
  }

  &__input {
    width: 100%;
    background: transparent;
    border: 0;
    border-bottom: 1px solid $primary;
    border-radius: 0;
    padding: 12px 0;
    font-size: 16px;
    line-height: 24px;
    color: #1b1c1c;
    outline: none;
    appearance: none;

    &::placeholder {
      color: $outline;
    }

    &--error {
      border-bottom-color: $error;
    }
  }

  &__textarea {
    width: 100%;
    background: transparent;
    border: 1px solid $primary;
    border-radius: 0;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 24px;
    color: #1b1c1c;
    outline: none;
    resize: vertical;

    &::placeholder {
      color: $outline;
    }

    &--error {
      border-color: $error;
    }
  }

  &__error {
    font-size: 12px;
    line-height: 16px;
    color: $error;
    margin: 6px 0 0;

    &--terms {
      margin-top: -24px;
      margin-bottom: 24px;
    }
  }

  // --- Submission block ---
  &__submit {
    padding-top: 32px;
    border-top: 1px solid $outline-variant;
  }

  &__terms {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 40px;
    cursor: pointer;
  }

  &__cb {
    position: relative;
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    margin-top: 2px;
  }

  &__cb-input {
    appearance: none;
    -webkit-appearance: none;
    position: absolute;
    inset: 0;
    width: 24px;
    height: 24px;
    margin: 0;
    border: 2px solid $primary;
    border-radius: 0;
    background: $on-primary;
    cursor: pointer;

    &:checked {
      background: $primary;
    }
  }

  &__cb-x {
    position: absolute;
    inset: 0;
    width: 24px;
    height: 24px;
    color: $on-primary;
    opacity: 0;
    pointer-events: none;
  }

  &__cb-input:checked ~ &__cb-x {
    opacity: 1;
  }

  &__terms-text {
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    letter-spacing: 0.05em;

    a {
      text-decoration: underline;
      color: inherit;

      &:hover {
        color: $primary;
      }
    }
  }

  &__btn {
    width: auto;
    padding: 20px 48px;
    background: $primary;
    color: $on-primary;
    border: none;
    border-radius: 0;
    font-size: 14px;
    line-height: 20px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    cursor: pointer;
    transition: opacity 0.2s, letter-spacing 0.2s;

    &:hover:not(:disabled) {
      opacity: 0.9;
      letter-spacing: 0.16em;
    }

    &:active:not(:disabled) {
      transform: scale(0.98);
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
    }

    @media (max-width: 767px) {
      width: 100%;
    }
  }
}
</style>
